/* -----------------------------
Data Adapters for SearchableTable

This file provides different data adapters that implement the common interface
for SearchableTable. Each adapter handles a different data source:

1. StaticDataAdapter - For in-memory JavaScript data arrays
2. PostgRESTDataAdapter - For PostgREST API endpoints
3. More adapters can be added here as needed

## Data Adapter Interface

All adapters must implement:
```javascript
{
  fetchData: async ({ query, filters, sort, page, pageSize, signal }) => {
    return {
      rows: [],      // Array of data objects
      total: 0,      // Total count (for pagination)
      error?: string // Optional error message
    }
  }
}
```

----------------------------- */

// --- Date utility functions for PostgREST adapter
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateTimestamps = (dateValue) => {
  const startOfDay = `${dateValue}T00:00:00`;
  const nextDay = new Date(dateValue);
  nextDay.setDate(nextDay.getDate() + 1);
  const startOfNextDay = `${formatLocalDate(nextDay)}T00:00:00`;
  return { startOfDay, startOfNextDay };
};

/* -----------------------------
StaticDataAdapter

For working with static JavaScript data arrays.
Provides client-side search, filtering, sorting, and pagination.

Usage:
```javascript
const adapter = createStaticDataAdapter({
  data: [
    { id: 1, name: "John", email: "john@example.com" },
    { id: 2, name: "Jane", email: "jane@example.com" }
  ],
  searchFields: ['name', 'email'] // Fields to search across
});
```
----------------------------- */
export function createStaticDataAdapter({ data, searchFields = [] }) {
  return {
    fetchData: async ({ query, filters, sort, page, pageSize }) => {
      try {
        let filteredData = [...data];

        // Apply text search
        if (query && query.trim() && searchFields.length > 0) {
          const searchTerm = query.toLowerCase().trim();
          filteredData = filteredData.filter((item) =>
            searchFields.some((field) => {
              const value = item[field];
              return value && String(value).toLowerCase().includes(searchTerm);
            }),
          );
        }

        // Apply filters
        if (filters && filters.length > 0) {
          filteredData = filteredData.filter((item) =>
            filters.every((filter) => {
              const { column, operator, value } = filter;
              const itemValue = item[column];

              switch (operator) {
                case "eq":
                  return String(itemValue) === String(value);
                case "not.eq":
                  return String(itemValue) !== String(value);
                case "ilike":
                  return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
                case "ilike.%*":
                  return String(itemValue).toLowerCase().startsWith(String(value).toLowerCase());
                case "ilike.*%":
                  return String(itemValue).toLowerCase().endsWith(String(value).toLowerCase());
                case "gt":
                  return Number(itemValue) > Number(value);
                case "gte":
                  return Number(itemValue) >= Number(value);
                case "lt":
                  return Number(itemValue) < Number(value);
                case "lte":
                  return Number(itemValue) <= Number(value);
                case "eq.true":
                  return itemValue === true || itemValue === "true";
                case "eq.false":
                  return itemValue === false || itemValue === "false";
                default:
                  return true;
              }
            }),
          );
        }

        // Apply sorting
        if (sort && sort.column) {
          filteredData.sort((a, b) => {
            const aVal = a[sort.column];
            const bVal = b[sort.column];

            let comparison = 0;
            if (aVal < bVal) comparison = -1;
            if (aVal > bVal) comparison = 1;

            return sort.direction === "desc" ? comparison * -1 : comparison;
          });
        }

        const total = filteredData.length;

        // Apply pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        return {
          rows: paginatedData,
          total,
        };
      } catch (error) {
        return {
          rows: [],
          total: 0,
          error: error.message,
        };
      }
    },
  };
}

/* -----------------------------
PostgRESTDataAdapter

For working with PostgREST API endpoints.
Handles URL building, request formatting, and response parsing.

Usage:
```javascript
const adapter = createPostgRESTDataAdapter({
  endpoint: "https://api.example.com",
  resource: "products",
  headers: { Authorization: "Bearer ..." },
  staticFilters: { "is_active": "eq.true" }, // Always applied
  columns: { id: { type: "number" }, name: { type: "string" } }
});
```
----------------------------- */
export function createPostgRESTDataAdapter({
  endpoint,
  resource,
  headers = {},
  staticFilters = {},
  columns = {},
  searchColumns = [],
}) {
  // Helper to convert filter to PostgREST format
  const filterToPostgREST = (filter) => {
    const { column, operator, value } = filter;
    const columnConfig = columns[column];
    const columnType = columnConfig?.type || "string";

    // Handle special ilike operator formats
    const ilikePatterns = {
      "ilike.%*": `ilike.${value}*`,
      "ilike.*%": `ilike.*${value}`,
      ilike: `ilike.*${value}*`,
    };

    if (ilikePatterns[operator]) {
      return { key: column, value: ilikePatterns[operator] };
    }

    // Handle date field filtering (supports eq)
    if (columnType === "date" && value) {
      const { startOfDay, startOfNextDay } = getDateTimestamps(value);

      switch (operator) {
        case "eq":
          return {
            key: column,
            value: `gte.${startOfDay}`,
            extraFilter: { key: column, value: `lt.${startOfNextDay}` },
          };
        case "gt":
          return { key: column, value: `gte.${startOfNextDay}` };
        case "lt":
          return { key: column, value: `lt.${startOfDay}` };
        default:
          return { key: column, value: `${operator}.${value}` };
      }
    }

    // Handle datetime field filtering (only gt/lt support)
    if (columnType === "datetime" && value) {
      // Handle both datetime-local format (YYYY-MM-DDTHH:MM) and date format (YYYY-MM-DD)
      const isDateFormat = !value.includes("T");

      if (isDateFormat) {
        // Date format - convert to date ranges
        const { startOfDay, startOfNextDay } = getDateTimestamps(value);
        return operator === "gt"
          ? { key: column, value: `gte.${startOfNextDay}` }
          : { key: column, value: `lt.${startOfDay}` };
      }

      // Datetime format - add seconds if missing and use precise datetime filtering
      const datetimeValue = value.includes(":") && value.split(":").length === 2 ? `${value}:00` : value;
      return { key: column, value: `${operator}.${datetimeValue}` };
    }

    return { key: column, value: `${operator}.${value}` };
  };

  // Normalize searchColumns to array format
  const normalizedSearchColumns = (() => {
    if (typeof searchColumns === "string") {
      return searchColumns
        .split(",")
        .map((col) => col.trim())
        .filter(Boolean);
    }
    if (Array.isArray(searchColumns)) {
      return searchColumns;
    }
    return [];
  })();

  return {
    fetchData: async ({ query, filters, sort, page, pageSize, signal }) => {
      try {
        // Build URL
        const base = `${endpoint.replace(/\/$/, "")}/${resource.replace(/^\//, "")}`;
        const url = new URL(base);

        // Set column selection
        url.searchParams.set("select", Object.keys(columns).join(",") || "*");

        // Add static filters
        Object.entries(staticFilters).forEach(([key, value]) => url.searchParams.set(key, value));

        // Add applied filters
        if (filters) {
          filters.forEach((filter) => {
            const result = filterToPostgREST(filter);
            url.searchParams.set(result.key, result.value);
            // Handle special case where we need an additional filter (date "eq" case)
            if (result.extraFilter) {
              url.searchParams.set(result.extraFilter.key, result.extraFilter.value);
            }
          });
        }

        // Add search query
        const searchQuery = query?.trim();
        if (searchQuery && normalizedSearchColumns.length) {
          const orTerms = normalizedSearchColumns
            .map((colName) => `${colName}.ilike.*${searchQuery.replaceAll("*", "%")}*`)
            .join(",");
          url.searchParams.set("or", `(${orTerms})`);
        }

        // Add sorting
        if (sort && sort.column) {
          url.searchParams.set("order", `${sort.column}.${sort.direction}`);
        }

        // Calculate range for pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        // Make request
        const res = await fetch(url.toString(), {
          method: "GET",
          headers: {
            // count=exact vs estimated; estimated: PostgREST uses the planner's row estimate -- much faster, but not exact
            Prefer: "count=estimated",
            ...headers,
            // PostgREST uses the standard Range header for pagination
            Range: `${from}-${to}`,
          },
          signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`${res.status} ${res.statusText}: ${text}`);
        }

        const data = await res.json();

        // Parse total count from Content-Range header
        const cr = res.headers.get("Content-Range");
        const totalStr = cr?.split("/")[1];
        const total = totalStr ? parseInt(totalStr, 10) : null;

        return {
          rows: Array.isArray(data) ? data : [],
          total,
        };
      } catch (error) {
        if (error?.name === "AbortError") {
          throw error;
        }
        return {
          rows: [],
          total: 0,
          error: error.message,
        };
      }
    },
  };
}
